'use client';

import { getFragmentData } from '@packages/gql/generated/shopping';
import { RecoProductCardFragmentDoc } from '@packages/gql/generated/shopping/RecoProductCardFragmentDoc';
import { PrudsysBrandToProductsDocument } from '@packages/gql/generated/shopping/PrudsysBrandToProductsDocument';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel/RecoCarousel.client';
import { RecoProductCard } from '../../RecoProductCard/RecoProductCard.client';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';
import { ExtendedRecoCardItem } from '../../ExtendedRecoCardItem/ExtendedRecoCardItem.client';

export type PrudsysBrandToProductsRecoSliderProps = Pick<
  RequestParams,
  'productId' | 'brand' | 'categoryIds' | 'brandId'
> &
  ProductsRecoSliderProps;

/* GraphQL */ `
  query PrudsysBrandToProducts(
    $productId: String!
    $brand: String!
    $brandId: String!
    $categoryIds: [String!]!
    $locale: String!
  ) {
    prudsysBrandToProducts(
      productId: $productId
      brand: $brand
      brandId: $brandId
      categoryIds: $categoryIds
      locale: $locale
    ) {
      title
      type
      recommendations {
        ...RecoProductCard
      }
      extendedViewUrl
    }
  }
`;
const RDE_TYPE: RdeType = 'prudsysBrandToProducts';

/**
 * PrudsysBrandToProductsRecoSlider component description displayed in storybook
 */
export const PrudsysBrandToProductsRecoSlider = ({
  backgroundColor,
  brand,
  brandId,
  categoryIds,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  preferTitle = false,
  productId,
  title,
}: PrudsysBrandToProductsRecoSliderProps) => {
  const { locale } = useIntl();
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: PrudsysBrandToProductsDocument,
    variables: {
      productId,
      brand,
      brandId,
      categoryIds,
      locale,
    },
    context: useMemo(
      () => ({ suspense: false, fetchOptions: { headers: { 'x-locale': locale } } }),
      [locale],
    ),
    pause: fetchOnThreshold ? !isInThreshold : false,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.prudsysBrandToProducts.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) => getFragmentData(RecoProductCardFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.prudsysBrandToProducts.type;

  useProductsRecoTracking({
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title: data?.prudsysBrandToProducts.title || '',
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  const extendedSlotLink = data?.prudsysBrandToProducts.extendedViewUrl;

  return (
    <RecoCarousel
      title={preferTitle ? title : data?.prudsysBrandToProducts.title || title}
      loadingState={!filteredRecommendations}
      backgroundColor={backgroundColor}
      ref={recoEl}
    >
      {filteredRecommendations?.map((recommendation, idx) => (
        <RecoProductCard
          // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          data={recommendation}
          onClick={(akl, sku) => {
            onProductClick?.(akl, sku);
          }}
          position={idx + 1}
          rdeTypeAbbr={rdeTypeAbbr}
        />
      ))}
      {extendedSlotLink && (
        <ExtendedRecoCardItem link={extendedSlotLink} rdeTypeAbbr={rdeTypeAbbr} />
      )}
    </RecoCarousel>
  );
};
